/**
 * GA4 event tracking helpers for SalaryLab calculators.
 *
 * Wraps window.gtag() with null-safety — no-ops when gtag is undefined
 * (dev/test environments without the GA snippet).
 *
 * Import directly into controllers; no Stimulus registration needed.
 */

function sendToGtag(...args) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag(...args)
  }
}

/**
 * Maps an annual salary to a band label for the salary_band dimension.
 */
export function deriveSalaryBand(annualSalary) {
  const n = Number(annualSalary)
  if (!Number.isFinite(n) || n <= 0) return undefined

  if (n < 30_000) return "under_30k"
  if (n < 50_000) return "30k_50k"
  if (n < 70_000) return "50k_70k"
  if (n < 100_000) return "70k_100k"
  if (n < 150_000) return "100k_150k"
  if (n < 200_000) return "150k_200k"
  return "over_200k"
}

/**
 * Fires `calculator_used` after a successful AJAX calculation.
 */
export function trackCalculatorUsed(calculatorType, { salaryAmount, payFrequency } = {}) {
  sendToGtag("event", "calculator_used", {
    calculator_type: calculatorType,
    salary_band: deriveSalaryBand(salaryAmount),
    pay_frequency: payFrequency || undefined,
  })
}

/**
 * Fires `share` when the user copies or shares a calculator link.
 */
export function trackShare(calculatorType, method) {
  sendToGtag("event", "share", {
    calculator_type: calculatorType,
    method,
  })
}

/**
 * Fires `begin_checkout` when the user requests a PDF report.
 */
export function trackPdfRequested(calculatorType, reportVariant) {
  sendToGtag("event", "begin_checkout", {
    calculator_type: calculatorType,
    report_variant: reportVariant,
  })
}

/**
 * Fires `file_download` when the PDF download actually starts.
 */
export function trackPdfDownloaded(calculatorType, reportVariant) {
  sendToGtag("event", "file_download", {
    calculator_type: calculatorType,
    report_variant: reportVariant,
  })
}

/**
 * Fires `begin_checkout` when the user starts an advisor session.
 */
export function trackAdvisorSessionStart(sessionType) {
  sendToGtag("event", "begin_checkout", {
    prompt_type: sessionType,
  })
}

/**
 * Fires `generate_lead` on the user's first message in an advisor session.
 */
export function trackAdvisorFirstMessage(sessionType) {
  sendToGtag("event", "generate_lead", {
    prompt_type: sessionType,
  })
}

/**
 * Fires `upgrade_prompt_shown` when an upgrade prompt is displayed.
 */
export function trackUpgradePromptShown(promptType, source) {
  sendToGtag("event", "upgrade_prompt_shown", {
    prompt_type: promptType,
    calculator_type: source,
  })
}

/**
 * Fires `upgrade_prompt_clicked` when the user clicks an upgrade CTA.
 */
export function trackUpgradePromptClicked(promptType, source) {
  sendToGtag("event", "upgrade_prompt_clicked", {
    prompt_type: promptType,
    calculator_type: source,
  })
}

/**
 * Fires `salary_lookup` when a user views salary data on a role page.
 */
export function trackSalaryLookup() {
  sendToGtag("event", "salary_lookup", {
    calculator_type: "salary_lookup",
  })
}
